/**
 * ProjectScopeOverview — the two questions you open a project to answer:
 * WHAT is in it, and WHERE is it.
 *
 * Replaces the old Program Progress bar, which reported one percentage over a
 * pile of hand-typed milestone rows. That number told you a project was "40%
 * done" without saying 40% of what, and it was only ever as good as whether
 * someone remembered to tick boxes.
 *
 * Left half  — the scope, as a row of icons read off the proposal. Renders,
 *              video, tours, floor plans, the platform, the campaign.
 * Right half — the deliveries, one row each. A project that sold VEXOS and
 *              visualization has two productions running at once, so it gets two
 *              rows, each showing only the stage it is in. These are the SAME
 *              stages the client sees on their tracker (the PM's V1/V2/Final
 *              stamps and the VEXOS manager's stage) — not a second status that
 *              can drift from it.
 */
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Images,
  Film,
  Rotate3d,
  LayoutGrid,
  Boxes,
  Globe,
  Map,
  Megaphone,
  Presentation,
  Plane,
  MapPin,
  Package,
  ArrowUpRight,
} from "lucide-react";
import { apiClient } from "../../App";

// Icon names come from the backend's scope catalog (services/proposal_milestones
// SCOPE_ITEM_DEFS) so a new scope item is one edit there plus one line here.
const ICONS = {
  Images,
  Film,
  Rotate3d,
  LayoutGrid,
  Boxes,
  Globe,
  Map,
  Megaphone,
  Presentation,
  Plane,
  MapPin,
};

// Rail status → how the stage pill reads. Deliberately the same three states the
// client tracker uses; anything else would be a second vocabulary.
const RAIL_TONE = {
  approved: "bg-green-100 text-green-700",
  delivered: "bg-blue-100 text-blue-700",
  pending: "bg-secondary text-muted-foreground",
};

function ScopeTile({ item }) {
  const Icon = ICONS[item.icon] || Package;
  return (
    <div
      className="flex w-[74px] shrink-0 flex-col items-center gap-1.5 text-center"
      title={item.qty ? `${item.label} — ${item.detail}` : `${item.label}${item.detail ? ` — ${item.detail}` : ""}`}
      data-testid={`scope-item-${item.key}`}
    >
      <div className="relative">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-border">
          <Icon className="h-[18px] w-[18px] text-foreground" strokeWidth={1.5} />
        </div>
        {item.qty ? (
          <span className="absolute -right-1.5 -top-1.5 min-w-[20px] rounded-full bg-foreground px-1.5 py-px text-center text-[11px] font-medium leading-[16px] text-background tabular-nums">
            {item.qty}
          </span>
        ) : null}
      </div>
      <span className="text-[10.5px] leading-[13px] text-muted-foreground line-clamp-2">
        {item.short_label || item.label}
      </span>
    </div>
  );
}

function RailRow({ rail }) {
  return (
    <div className="flex items-center justify-between gap-3" data-testid={`rail-${rail.key}`}>
      <div className="min-w-0">
        <p className="truncate text-[13px] font-medium text-foreground">{rail.label}</p>
        <p className="truncate text-xs text-muted-foreground">{rail.round_title}</p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {/* Position in the rail — three deliveries, so three dots. */}
        <div className="flex items-center gap-1">
          {rail.rounds.map((r) => (
            <span
              key={r.key}
              title={`${r.label} — ${r.status}`}
              className={`h-1.5 w-1.5 rounded-full ${
                r.status === "approved"
                  ? "bg-green-600"
                  : r.status === "delivered"
                  ? "bg-blue-600"
                  : "bg-border"
              }`}
            />
          ))}
        </div>
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${RAIL_TONE[rail.status] || RAIL_TONE.pending}`}>
          {rail.round_label}
        </span>
      </div>
    </div>
  );
}

export function ProjectScopeOverview({ programId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    apiClient
      .get(`/programs/${programId}/scope-overview`)
      .then((res) => { if (mounted) setData(res.data); })
      .catch((e) => console.error("Failed to fetch scope overview", e))
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [programId]);

  if (loading) {
    return <div className="h-[132px] animate-pulse rounded-2xl border border-border" />;
  }
  if (!data) return null;

  const { scope_items: scope = [], rails = [], scope_source } = data;

  return (
    <div className="rounded-2xl border border-border" data-testid="scope-overview">
      <div className="grid gap-5 p-5 sm:p-6 md:grid-cols-2 md:gap-0">
        {/* ── What's in it ── */}
        <div className="md:pr-6">
          <div className="flex items-center justify-between gap-2">
            <span className="label-text">Scope</span>
            {scope_source === "scope_modules" && (
              <span className="text-[11px] text-muted-foreground" title="No proposal is linked to this project, so this is the coarser scope it was activated with.">
                from scope modules
              </span>
            )}
          </div>
          {scope.length === 0 ? (
            <p className="mt-4 text-[13px] text-muted-foreground">
              No scope on file. Link this project's proposal to pull it in.
            </p>
          ) : (
            <div className="mt-4 flex flex-wrap gap-x-3 gap-y-4">
              {scope.map((item) => (
                <ScopeTile key={item.key} item={item} />
              ))}
            </div>
          )}
        </div>

        {/* ── Where it is ── */}
        <div className="border-t border-border pt-5 md:border-l md:border-t-0 md:pl-6 md:pt-0">
          <div className="flex items-center justify-between gap-2">
            <span className="label-text">Delivery</span>
            <Link
              to={`/programs/${programId}/tracker`}
              className="flex items-center gap-0.5 text-[11px] text-muted-foreground transition-colors hover:text-foreground"
            >
              Client tracker
              <ArrowUpRight className="h-3 w-3" />
            </Link>
          </div>
          {rails.length === 0 ? (
            <p className="mt-4 text-[13px] text-muted-foreground">No production rails on this project yet.</p>
          ) : (
            <div className="mt-4 space-y-3">
              {rails.map((rail) => (
                <RailRow key={rail.key} rail={rail} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProjectScopeOverview;
